import { Button } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import MenuIcon from '@material-ui/icons/Menu';
import clsx from 'clsx';
import type { CSSProperties, MouseEventHandler } from 'react';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useAuth } from '../../auth';
import { ReactComponent as MogulIcon } from '../../common/svg/general/logo-mogul.svg';
import { useWeb3State } from '../../hooks';
import { WEB3_CONFIG } from '../../lib';
import { Footer, Header } from './components';
import styles from './Sidebar.module.css';

const NON_REACT_ICONS = [
  'Marketplace',
  'Submit Project',
  'Sign out',
  'BUY STARS',
  'Launchpads',
  'Staking',
];

const MENU_NON_PRIMARY_STYLES: CSSProperties = {
  width: '100%',
  borderWidth: '2px',
  height: '45px',
  fontSize: '12px',
  fontWeight: 'bold',
  marginTop: '0.3rem',
  borderRadius: '2rem',
  marginRight: '-0.45rem',
  marginLeft: '0.3rem',
  borderColor: '#FFFFFF',
  color: '#FFFFFF',
};

const MENU_PRIMARY_STYLES: CSSProperties = {
  width: '100%',
  borderWidth: '2px',
  height: '45px',
  fontSize: '12px',
  fontWeight: 'bold',
  marginTop: '0.3rem',
  borderRadius: '2rem',
  marginRight: '-0.45rem',
  marginLeft: '0.3rem',
  borderColor: '#FCD559',
  color: '#FCD559',
};

interface MenuItem {
  title: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ElementType;
  button?: boolean;
  type?: string;
  subtabs?: MenuItem[];
}

interface Props {
  profileImageSrc?: string;
  menuItems?: MenuItem[];
  menuItemsBottom?: MenuItem[];
}

export const SidebarComponent: React.FC<Props> = ({
  profileImageSrc,
  menuItems,
  menuItemsBottom,
}) => {
  const { authenticated, user } = useAuth();
  const { walletDetails } = useWeb3State();
  const [isOpen, setIsOpen] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  // const [starsPrice, setStarsPrice] = React.useState(0);

  const container =
    window !== undefined ? () => window.document.body : undefined;

  const handleDrawerChange = (value: boolean) => {
    setIsOpen(value);
  };

  const handleDrawerToggle = () => {
    setIsMobileOpen(!isMobileOpen);
    handleDrawerChange(true);
  };

  const onLinkClick = (menu: MenuItem) => {
    if (menu.title === 'Gitbook' && menu.href != null) {
      window.open(menu.href, '_blank');
    } else if (menu.title === 'Mint' && menu.href != null) {
      window.location.assign(menu.href);
    } else if (menu.href != null) {
      history.push(menu.href);
    }

    menu.onClick?.();
  };

  const renderMenuItems = (items: MenuItem[], isSubtabs = false) => {
    const isBsc = walletDetails.chain.id === WEB3_CONFIG.chains.bsc.id;

    // Remove unused sections for Binance smart chain
    let filteredItems = items;
    if (isBsc) {
      filteredItems = filteredItems.filter(
        item => item.title !== 'Auction House',
      );
    }
    // No sign out button when signed in
    if (!authenticated) {
      filteredItems = filteredItems.filter(
        item => item.title !== 'Sign out' && item.title !== 'Community',
      );
    }
    // creator dashboard for admin role only
    if (!user || user.role !== 'ADMIN' || !authenticated) {
      filteredItems = filteredItems.filter(
        item => item.title !== 'Creators Dashboard',
      );
    }

    const getButton = (menuItem: MenuItem) => {
      return (
        <>
          {isOpen ? (
            <Button
              variant="outlined"
              style={
                menuItem.type === 'primary'
                  ? MENU_PRIMARY_STYLES
                  : MENU_NON_PRIMARY_STYLES
              }
              onClick={() => {
                menuItem.onClick?.();
                setIsMobileOpen(false);
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
              >
                {menuItem.icon && (
                  <menuItem.icon
                    className={`${styles.nonReactIcon}`}
                    style={{
                      marginLeft: '0',
                      marginRight: '0.8rem',
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'transparent',
                    }}
                  />
                )}
                {menuItem.title}
              </div>
            </Button>
          ) : (
            <ListItemIcon>
              {menuItem.icon && (
                <menuItem.icon
                  className={`${styles.nonReactIcon}`}
                  style={{
                    marginLeft: '0.35rem',
                    width: '20px',
                    height: '20px',
                  }}
                />
              )}
            </ListItemIcon>
          )}
        </>
      );
    };

    const MenuButton: React.FC<{ menuItem: MenuItem; buttonVal: any }> = ({
      menuItem,
      buttonVal,
    }) => {
      const isSelected = location.pathname === menuItem.href;
      const isSubTabSelected = menuItem.subtabs?.some(
        subtab => location.pathname === subtab.href,
      );

      const [isCollapsed, setIsCollapsed] = useState(!isSubTabSelected);

      return (
        <>
          <ListItem
            {...(menuItem.href?.includes('http') && {
              component: 'a',
              href: menuItem.href,
              target: '_blank',
            })}
            style={{
              paddingLeft:
                menuItem.title === 'Mogies' && isOpen
                  ? '20px'
                  : isSubtabs && isOpen
                  ? '42px'
                  : (isSubtabs || menuItem.title === 'Mogies') && !isOpen
                  ? '24px'
                  : '20px',
              paddingTop: isSubtabs && isOpen ? 0 : '8px',
              paddingBottom: isSubtabs && isOpen ? 0 : '8px',
            }}
            {...buttonVal}
            key={`${menuItem.href ?? ''}:${menuItem.title}`}
            onClick={() => {
              // List Item for 'BUY STARS' does not redirect to link if sidebar not collapsed
              if (menuItem.title !== 'BUY STARS') {
                onLinkClick(menuItem);
              }

              // List Item for 'BUY STARS' redirects to link if sidebar is collapsed
              if (menuItem.title === 'BUY STARS' && !isOpen) {
                onLinkClick(menuItem);
              }
            }}
            className={clsx(styles.menuItem, {
              [styles.selectedListItem]: isSelected,
            })}
          >
            {menuItem.button ? (
              getButton(menuItem)
            ) : menuItem.title.includes('Buy') ? (
              <>
                <ListItemIcon style={{ paddingLeft: '5px' }}>
                  {menuItem.icon && <menuItem.icon />}
                </ListItemIcon>
                <ListItemText
                  primary={menuItem.title}
                  className={`${styles.menuItemTitle} -ml-1`}
                />
              </>
            ) : (
              <>
                <ListItemIcon
                  style={{
                    minWidth: isSubtabs && isOpen ? '38px' : '56px',
                  }}
                >
                  {menuItem.icon &&
                    (NON_REACT_ICONS.includes(menuItem.title) ? (
                      <menuItem.icon className={`${styles.nonReactIcon}`} />
                    ) : (isSubtabs || menuItem.title === 'Mogies') &&
                      !isOpen ? (
                      <menuItem.icon width={21} style={{ width: '21px' }} />
                    ) : menuItem.title === 'Mogieland' ? (
                      <menuItem.icon style={{ marginLeft: '7px' }} />
                    ) : (
                      <menuItem.icon className={'ml-1'} />
                    ))}
                </ListItemIcon>
                <ListItemText
                  primary={!isOpen ? '' : menuItem.title}
                  className={styles.menuItemTitle}
                />
                {menuItem.subtabs && (
                  <IconButton
                    onClick={
                      (e => {
                        setIsCollapsed(!isCollapsed);
                        e.stopPropagation();
                      }) as MouseEventHandler<HTMLButtonElement>
                    }
                    size="small"
                    style={{
                      marginRight: isSelected ? 0 : 4, // I am sorry
                    }}
                  >
                    {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                  </IconButton>
                )}
              </>
            )}
          </ListItem>
          {!isCollapsed &&
            isOpen &&
            menuItem.subtabs &&
            renderMenuItems(menuItem.subtabs, true)}
        </>
      );
    };

    return (
      <div>
        {filteredItems.map(menuItem => {
          let buttonVal: any = { button: true };

          // List Item for 'BUY STARS' set as not clickable if sidebar not collapsed
          if (menuItem.button && isOpen) {
            buttonVal = { button: false };
          }
          return (
            <MenuButton
              menuItem={menuItem}
              buttonVal={buttonVal}
              key={menuItem.title}
            />
          );
        })}
      </div>
    );
  };

  const renderDrawer = () => {
    return (
      <>
        <Header
          isOpen={isOpen}
          setMobileOpen={setIsMobileOpen}
          profileImage={profileImageSrc}
        />
        {menuItems && (
          <div className={styles.menuItemsTop}>
            <List>{renderMenuItems(menuItems)}</List>
          </div>
        )}
        {menuItemsBottom && (
          <div className={styles.menuItemsBottom}>
            <List>{renderMenuItems(menuItemsBottom)}</List>
          </div>
        )}
        <Footer isOpen={isOpen} />
      </>
    );
  };

  const spacerCn = isOpen ? styles.sideBarSpacer : styles.sideBarSpacerClosed;

  return (
    <>
      <div className={styles.sidebar}>
        <Hidden xsDown implementation="css">
          <>
            <div className={styles.drawerCollapse}>
              <IconButton onClick={() => handleDrawerChange(!isOpen)}>
                {isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </IconButton>
            </div>
            <Drawer
              variant="permanent"
              className={clsx(styles.sidebarDrawer, styles.drawerDiv, {
                [styles.drawerOpen]: isOpen,
                [styles.drawerClose]: !isOpen,
              })}
              open
            >
              {renderDrawer()}
            </Drawer>
          </>
        </Hidden>
        <Hidden smUp implementation="css">
          <IconButton
            color="inherit"
            edge="start"
            className={styles.drawerHome}
            onClick={() => history.push(`/`)}
          >
            <MogulIcon />
          </IconButton>
          <IconButton
            color="inherit"
            edge="start"
            className={styles.drawerToggle}
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            variant="temporary"
            container={container}
            open={isMobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
          >
            <div className={styles.sidebarSM}>{renderDrawer()}</div>
          </Drawer>
        </Hidden>
      </div>
      <div className={spacerCn} />
    </>
  );
};
